import type { DesignSystemMention } from "@afar-research/contracts";

import type { DesignAgentActionId } from "./designActions.ts";
import { splitDesignBrief } from "./designActions.ts";
import type { ThreadOperationSummaryInput } from "./operationIdentity.ts";

/**
 * What the user actually typed into the composer, and what kind of thing it is.
 * Create types a *name*, not a brief, so a surface that labelled every Design
 * body "Brief" would be wrong for it — the distinction is here rather than in
 * the drawing, because it is a fact about the operation.
 */
export type DesignOperationBody =
  | { readonly kind: "brief"; readonly text: string }
  /** Create's display name. */
  | { readonly kind: "name"; readonly text: string };

/**
 * What a Design Operation is *about*, derived from the operation itself rather
 * than from the prompt `formatDesignOperationPrompt` arranged for the provider.
 *
 * Deliberately not a `ThreadQueueOperationSummary`: Brain draws an operation
 * headline over an elided item list, and Design draws the operation as chrome
 * around the user's own words. The two agree on deriving from the
 * operation and disagree on everything past it, so one return type would cost a
 * union inside every field rather than save a type.
 *
 * The operation's copy is not carried here — `describeDesignComposerOperation` owns
 * the label, the short label and the blurb, and a surface holding the `action`
 * reads whichever of them its width allows.
 */
export interface DesignOperationSummary {
  readonly action: DesignAgentActionId;
  /** The Design System Mention the operation points at; `null` only for Create. */
  readonly target: DesignSystemMention | null;
  /** `null` when nothing was typed — Add Template, typically, whose name the skill grills for. */
  readonly body: DesignOperationBody | null;
  /** Add Example's `Add ▸ URL` entries, split back out of the brief they fold into. */
  readonly evidenceUrls: ReadonlyArray<string>;
}

/**
 * A Design Operation's summary, or `null` for anything that has to keep
 * rendering its own text: a Brain operation, a Delete — which never becomes a
 * queued submission and reports through its own timeline row — and a Design
 * identity persisted before the Design System Mention reshape, which lost its
 * payload and keeps its historical bubble.
 */
export function summarizeDesignOperation(
  operation: ThreadOperationSummaryInput,
): DesignOperationSummary | null {
  switch (operation.action) {
    case "create-design":
      return "displayName" in operation
        ? {
            action: "create-design",
            target: null,
            body: { kind: "name", text: operation.displayName },
            evidenceUrls: [],
          }
        : null;
    case "update-design":
    case "add-design-template":
    case "add-design-example":
    case "create-design-sample":
    case "rename-design-system": {
      if (!("target" in operation)) return null;
      // A rename sent before the name became a brief carried the user's words
      // in `nextDisplayName`. They were still the user's words, so they read as
      // the brief rather than as a name the app would now never have invented.
      const typed =
        "brief" in operation
          ? operation.brief
          : "nextDisplayName" in operation
            ? operation.nextDisplayName
            : undefined;
      const { prose, evidenceUrls } = splitDesignBrief(typed);
      return {
        action: operation.action,
        target: operation.target,
        body: prose.length > 0 ? { kind: "brief", text: prose } : null,
        evidenceUrls,
      };
    }
    default:
      return null;
  }
}
